/**
 * Координатор refresh токена между вкладками браузера.
 *
 * Проблема: при 10+ открытых вкладках и протухании токена каждая вкладка
 * независимо пыталась обновить токен, что приводило к ошибкам (refresh_token
 * обычно одноразовый) и выбрасывало пользователя.
 *
 * Решение: теперь только одна вкладка делает refresh, остальные ждут.
 *
 * Используется в:
 * - socket.ts (AppSocket.refreshToken) — при TOKEN_EXPIRED от WebSocket
 * - request.ts (refreshTokenAndRetry) — при 401 от HTTP запросов
 *
 * Пример использования:
 *   if (!tokenRefreshCoordinator.tryAcquireLock()) {
 *     await tokenRefreshCoordinator.waitForRefresh();
 *     return; // токен уже обновлён другой вкладкой
 *   }
 *   // делаем refresh...
 *   tokenRefreshCoordinator.notifyRefreshComplete();
 */

const REFRESH_LOCK_KEY = 'token_refresh_lock';
const REFRESH_CHANNEL = 'token_refresh_channel';
const LOCK_TIMEOUT = 10000; // 10 секунд максимум на refresh

/*** tryAcquireLock():
 - Проверяет localStorage
 - Если лок есть и не протух (< 10 сек) → return false
 - Иначе ставит лок → return true

 waitForRefresh():
 - Ждёт через BroadcastChannel (мгновенно) ИЛИ polling (100мс) ИЛИ таймаут (10 сек)
 - Кто первый — тот и резолвит Promise

 notifyRefreshComplete():
 - Отправляет BroadcastChannel + удаляет лок ***/

/**
 * Координатор refresh токена между вкладками браузера.
 * Использует localStorage как лок и BroadcastChannel для уведомлений.
 */
class TokenRefreshCoordinator {
  private channel: BroadcastChannel | null = null;
  private waitingResolvers: Set<() => void> = new Set();

  constructor() {
    if (typeof BroadcastChannel !== 'undefined') {
      this.channel = new BroadcastChannel(REFRESH_CHANNEL);

      this.channel.onmessage = (event) => {
        if (event.data?.type === 'refresh_complete') {
          this.waitingResolvers.forEach((resolve) => resolve());
          this.waitingResolvers.clear();
        }
      };
    }
  }

  /**
   * Пытается получить лок на refresh токена между вкладками.
   * Возвращает true если эта вкладка должна делать refresh.
   */
  tryAcquireLock(): boolean {
    const now = Date.now();
    const lockData = localStorage.getItem(REFRESH_LOCK_KEY);

    if (lockData) {
      try {
        const { timestamp } = JSON.parse(lockData);
        //Если лок не протух — другая вкладка уже делает refresh
        if (now - timestamp < LOCK_TIMEOUT) {
          return false;
        }
      } catch {
        // Лок протух — будет установлен/перезаписан ниже
      }
    }

    localStorage.setItem(REFRESH_LOCK_KEY, JSON.stringify({ timestamp: now }));
    return true;
  }

  releaseLock(): void {
    localStorage.removeItem(REFRESH_LOCK_KEY);
  }

  notifyRefreshComplete(): void {
    this.channel?.postMessage({ type: 'refresh_complete' });
    this.releaseLock();
  }

  /**
   * Ждёт пока другая вкладка завершит refresh.
   */
  async waitForRefresh(): Promise<void> {
    return new Promise((resolve) => {
      let resolved = false;

      const done = () => {
        if (resolved) return;
        resolved = true;
        clearInterval(checkInterval);
        this.waitingResolvers.delete(done);
        resolve();
      };

      // BroadcastChannel — мгновенное уведомление
      this.waitingResolvers.add(done);

      // Fallback: polling localStorage
      const checkInterval = setInterval(() => {
        if (!localStorage.getItem(REFRESH_LOCK_KEY)) {
          done();
        }
      }, 100);

      // Таймаут
      setTimeout(done, LOCK_TIMEOUT);
    });
  }
}

export const tokenRefreshCoordinator = new TokenRefreshCoordinator();
